import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  Quantity,
  RangeQuantity,
  Seal,
  toTransportType,
  Transport,
  TransportEntity, TransportPayload,
  transportSelector,
  TransportType,
  transportType,
  useGetTransportQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  getTransportPatch,
  TransportForm,
  TransportFormOptions,
  TransportFormValues
} from "../../components/form/TransportForm/TransportForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useTransportMutation} from "../../hooks/useTransportMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {TransportGaugeFormValues} from "../../components/form/TransportGaugeForm/TransportGaugeForm";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  type?: TransportType;
  id?: string;
  copy?: boolean;
  options?: TransportFormOptions,
  formDefaults?: Partial<TransportFormValues>
}

export const TransportFormPageRoute = () => {
  const {state} = useLocation();

  const type = state?.type;
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  const options = state?.options;

  return (
    <TransportFormPage
      type={type ? toTransportType(type) : undefined}
      id={id}
      copy={copy}
      formDefaults={formDefaults}
      options={options}
    />
  );
};

export const toFormDefaultsFromTransport = (transport: TransportEntity | undefined): Partial<TransportFormValues> => {
  let quantity: Quantity | undefined = undefined;
  let temperature: Quantity | undefined = undefined
  let odometer: Quantity | undefined = undefined
  let meterReading: Partial<RangeQuantity> | undefined = undefined
  let seal: Partial<Seal> | undefined = undefined
  let seal2: Partial<Seal> | undefined = undefined
  let seal3: Partial<Seal> | undefined = undefined
  let gauges: TransportGaugeFormValues[] = []

  if (transport) {
    if (typeof transport.quantityDecimal !== 'undefined' && transport.quantityUnitOfMeasure) {
      quantity = {
        number: transport.quantityDecimal,
        units: transport.quantityUnitOfMeasure
      };
    }

    if (typeof transport.observedTemperature !== 'undefined' && transport.temperatureUom) {
      temperature = {
        number: transport.observedTemperature,
        units: transport.temperatureUom
      };
    }

    if (typeof transport.odometer !== 'undefined'  && transport.odometerUom) {
      odometer = {
        number: transport.odometer,
        units: transport.odometerUom
      };
    }

    if (typeof transport.meterReadingStart !== 'undefined'  || typeof transport.meterReadingEnd !== 'undefined' ) {
      meterReading = {
        start: transport.meterReadingStart,
        end: transport.meterReadingEnd,
        units: transport.meterReadingUom,
      }
    }

    // TODO: seal dates
    seal = {
      off: transport.sealOff,
      on: transport.sealOn,
      valveType: transport.sealValveType,
    }

    seal2 = {
      off: transport.seal2Off,
      on: transport.seal2On,
      valveType: transport.seal2ValveType,
    }

    seal3 = {
      off: transport.seal3Off,
      on: transport.seal3On,
      valveType: transport.seal3ValveType,
    }

    if (transport.gauges) {
      for (const gauge of transport.gauges) {
        gauges.push({
          container: gauge.containerId,
          type: gauge.type,
          topValue: gauge.topValue,
          bottomValue: gauge.bottomValue
        })
      }
    }
  }

  return {
    date: adjustFromTimeZoneSafely(transport?.createDttm),
    inDate: adjustFromTimeZoneSafely(transport?.inDttm),
    outDate: adjustFromTimeZoneSafely(transport?.outDttm),
    disableDemurrage: transport?.disableDemurrage,
    skipGeofenceEvent: transport?.skipGeofenceEvent,
    person: transport?.personId,
    vehicle: transport?.vehicleId,
    trailer: transport?.trailerId,
    odometer: odometer,
    quantity: quantity,
    rejectionType: transport?.rejectionType,
    rejectionComment: transport?.rejectionComment,
    netQuantity: transport?.netQuantity,
    observedGravity: transport?.observedGravity,
    correctedGravity: transport?.correctedGravity,
    bswPercentage: transport?.bswPercentage,
    observedTemperature: temperature,
    topTemperature: transport?.topTemperature,
    bottomTemperature: transport?.bottomTemperature,
    freightType: transport?.waterTypeId,
    serviceType: transport?.serviceTypeId,
    site: transport?.siteId,
    well: transport?.wellId,
    container: transport?.containerId,
    ticketNumber: transport?.ticketNumber,
    workOrderNumber: transport?.workOrderNumber,
    internalWorkOrderNumber: transport?.internalWorkOrderNumber,
    regulatoryNumber: transport?.regulatoryNumber,
    meterReading: meterReading,
    seal: seal,
    seal2: seal2,
    seal3: seal3,
    gauges: gauges,
    comments: undefined
  };
};

export const TransportFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
    type = transportType.pickup,
    options,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectTransport = useMemo(() => {
    return transportSelector()
  }, [])

  const {currTransport, isLoading} = useGetTransportQuery(access ? (id ? {id} : skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currTransport: selectTransport(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<TransportFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromTransport(currTransport);
  }, [currTransport, formDefaults]);

  const isEdit = useMemo(() => {
    return currTransport && !copy
  }, [currTransport, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.transport.form.title.edit", "Edit Transport", {ns: "common"});
    } else {
      return t("page.entity.transport.form.title.new", "New Transport", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createTransport,
    updateTransport,
    isCreating,
    isUpdating,
  } = useTransportMutation()

  const onTransportSubmit = useCallback((formValues: TransportFormValues) => {
    const patch = getTransportPatch(formValues, regionId)

    if (isEdit) {
      const transport: TransportPayload = {
        ...currTransport.asTypeValue(),
        ...patch,
      };
      updateTransport(transport, () => navigate(-1))
    } else {
      const transport: TransportPayload = {
        ...patch,
        type: type?.code,
      };
      createTransport(transport, () => navigate(-1))
    }
  }, [regionId, currTransport, isEdit, updateTransport, navigate, createTransport, type]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "transport_edit" : "transport_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <TransportForm
                  type={type}
                  options={{
                    ...options,
                    regionId: options?.regionId ?? currTransport?.regionId ?? regionId,
                    hasGeofenceEvent: options?.hasGeofenceEvent ?? typeof currTransport?.geofenceEvent !== "undefined"
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onTransportSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

